import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChange,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NgControl,
} from '@angular/forms';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { MiqBehaviors } from '@shared/behaviors';
import { simpleValueValidation } from '@shared/utils/helpers/records.helpers';
import { isMasked } from '@shared/utils/helpers/sanitizers';
import { Moment } from 'moment';
import moment from 'moment-timezone';
import { TextInput } from '../text-input';

const UTC_EDP_INPUT_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
const UTC_EDP_OUTPUT_FORMAT = 'YYYY-MM-DDTHH:mm:ss[Z]';

@Component({
  selector: 'miq-date',
  templateUrl: './date.component.html',
  styleUrls: ['./date.component.scss'],
  providers: [{ provide: MiqBehaviors, useExisting: DateComponent }],
  standalone: false,
})
export class DateComponent extends TextInput implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() placeholder = '';
  @Input() handleTimezone = false;
  @Input() readonly = false;
  @Input() set minDate(minDate: string | moment.Moment) {
    if (!!minDate && !this.isMaskedValue && !DateComponent.isValidDate(minDate)) {
      const newDate = moment(minDate, UTC_EDP_INPUT_FORMAT);

      let validDate = null;
      if (DateComponent.isValidDate(newDate)) {
        validDate = this.handleTimezone ? newDate : newDate.utc();
      }

      this.configuredMinDate = validDate;
    }
  }
  @Input() panelClass: string;

  @Output() currentValueChange = new EventEmitter<any>();
  @Output() formControlCreated: EventEmitter<UntypedFormControl> = new EventEmitter<UntypedFormControl>();

  @ViewChild(MatInput, { static: true }) matInput: MatInput;
  @ViewChild('picker') picker: MatDatepicker<any>;

  public configuredMinDate: Date = null;

  protected onTouched: Function;

  private dateValue: string | moment.Moment;
  private onChange: Function;

  set date(date: string | moment.Moment) {
    let resultDate = date;
    if (!!date && !this.isMaskedValue && !DateComponent.isValidDate(date)) {
      const newDate = moment(date, UTC_EDP_INPUT_FORMAT);

      let validDate = null;
      if (DateComponent.isValidDate(newDate)) {
        validDate = this.handleTimezone ? newDate : newDate.utc();
      }

      resultDate = validDate;
    }
    this.dateValue = resultDate;
  }

  get date(): string | moment.Moment {
    return this.dateValue;
  }

  /**
   * Validate if a date is a valid moment date
   *
   * @param value Value to validate
   */
  private static isValidDate(value: any): boolean {
    return !!value && moment.isMoment(value) && value.isValid();
  }

  constructor(
    @Optional() private controlContainer: ControlContainer,
    @Self() @Optional() private ngControl: NgControl
  ) {
    super();
    if (!!ngControl) {
      ngControl.valueAccessor = this;
    }
    this.onChange = this.onDateChange.bind(this);
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    const { currentValue } = changes;
    if (!!currentValue && !currentValue.firstChange && this.isDateValueDifferentOrWasMasked(currentValue)) {
      this.date = this.currentValue;
      this.fc.setValue(this.date);
      this.fc.markAsPristine();
    }
  }

  /**
   * This validates if component needs to update the form control value it also prevents
   * a loop on client components using the currentValue input & currentValueChange event emitter
   */
  private isDateValueDifferentOrWasMasked(change: SimpleChange): boolean {
    return (
      (isMasked(change.previousValue) && !isMasked(change.currentValue)) ||
      (moment.isMoment(this.dateValue) && this.dateValue.format(UTC_EDP_OUTPUT_FORMAT) !== change.currentValue) ||
      (!this.dateValue && change.currentValue)
    );
  }

  /**
   * fc control is created in initFormControl method which is invoked
   * in super class InputBase by calling initFormControl
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.formControlCreated.emit(this.fc);
  }

  ngOnDestroy(): void {
    this.matInput = null;
  }

  protected initFormControl() {
    this.date = this.currentValue;
    this.fc = !!this.ngControl
      ? ((this.controlContainer.control as UntypedFormGroup).controls[this.ngControl.name] as UntypedFormControl)
      : new UntypedFormControl(this.controlValues(), this.controlValidators());
  }

  /**
   * Focus on input
   */
  focus() {
    this.matInput.focus();
  }

  /**
   * Used by Angular to register a function to call on save
   *
   * @param fn Angular function
   */
  registerOnChange(fn: any): void {
    this.onChange = (newValue: string | moment.Moment) => fn(this.onDateChange(newValue as any));
  }

  /**
   * Used by Angular to register a function to call on touched
   *
   * @param fn Angular function
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Used by angular to update value
   *
   * @param date New date value
   */
  writeValue(date: string | moment.Moment): void {
    this.onChange(date);
  }

  /**
   * Used by angular to set control as disabled
   *
   * @param isDisabled Is disabled value
   */
  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.fc.disable();
    } else {
      this.fc.enable();
    }
  }

  /**
   * Main on change value transformation method. It set a new value to date
   * property. Also emit transformed date value to string adding or not any
   * timezone.
   *
   * @param newValue New value to display
   * @return Transformed date
   */
  onDateChange(change: MatDatepickerInputEvent<Moment>) {
    this.date = change.value;
    const date = !simpleValueValidation(this.date) ? this.date : this.date.format(UTC_EDP_OUTPUT_FORMAT);
    const finalDate = this.valueSetter ? this.valueSetter(date) : date;
    this.currentValueChange.emit(finalDate);
    return this.date;
  }
}
